import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarDays, CalendarRange, BookOpen, Plus, Pencil, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { SchoolCalendarTab } from "@/components/SchoolCalendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const AREA_LABELS = { LINGUAGENS: "Linguagens", MATEMATICA: "Matemática", NATUREZA: "Ciências da Natureza", HUMANAS: "Ciências Humanas", OUTRAS: "Outras" };

export default function AcademicConfig() {
  const { user, scopeParams, selectedSchoolId, isSuperadmin } = useAuth();
  const [years, setYears] = useState(null);
  const [subjects, setSubjects] = useState(null);

  const fetchAll = () => {
    api.get("/academic-years", { params: scopeParams }).then((r) => setYears(r.data));
    api.get("/subjects", { params: scopeParams }).then((r) => setSubjects(r.data));
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [selectedSchoolId]);

  const needsSchool = isSuperadmin && !selectedSchoolId;
  const canCalendar = ["SUPERADMIN", "DIRECAO", "COORDENACAO"].includes(user?.role);

  return (
    <div>
      <PageHeader title="Configurações Acadêmicas" subtitle="Anos letivos, trimestres e disciplinas" />
      {needsSchool && <Card className="mb-4 border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">Selecione uma escola no topo para gerenciar as configurações.</Card>}
      <Tabs defaultValue="years">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 sm:w-auto">
          <TabsTrigger value="years" data-testid="academic-year-tab"><CalendarDays className="mr-1.5 h-4 w-4" /> Anos Letivos</TabsTrigger>
          <TabsTrigger value="subjects" data-testid="subjects-tab"><BookOpen className="mr-1.5 h-4 w-4" /> Disciplinas</TabsTrigger>
          {canCalendar && <TabsTrigger value="calendar" data-testid="school-calendar-tab-trigger"><CalendarRange className="mr-1.5 h-4 w-4" /> Calendário SEEDUC</TabsTrigger>}
        </TabsList>
        <TabsContent value="years" className="mt-4"><YearsTab years={years} onChange={fetchAll} disabled={needsSchool} /></TabsContent>
        <TabsContent value="subjects" className="mt-4"><SubjectsTab subjects={subjects} onChange={fetchAll} disabled={needsSchool} /></TabsContent>
        {canCalendar && <TabsContent value="calendar" className="mt-4"><SchoolCalendarTab years={years} disabled={needsSchool} /></TabsContent>}
      </Tabs>
    </div>
  );
}

function YearsTab({ years, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ year: new Date().getFullYear(), status: "ATIVO", terms: [] });
  const [saving, setSaving] = useState(false);

  const defaultTerms = [
    { name: "1º TRIMESTRE", order: 1, start_date: "", end_date: "" },
    { name: "2º TRIMESTRE", order: 2, start_date: "", end_date: "" },
    { name: "3º TRIMESTRE", order: 3, start_date: "", end_date: "" },
  ];
  const openNew = () => { setEditing(null); setForm({ year: new Date().getFullYear(), status: "ATIVO", terms: defaultTerms }); setOpen(true); };
  const openEdit = (y) => { setEditing(y); setForm({ ...y, terms: y.terms?.length ? y.terms : defaultTerms }); setOpen(true); };

  const setTerm = (i, k, v) => setForm((f) => { const t = [...f.terms]; t[i] = { ...t[i], [k]: v }; return { ...f, terms: t }; });
  const addTerm = () => setForm((f) => ({ ...f, terms: [...f.terms, { name: `${f.terms.length + 1}º TRIMESTRE`, order: f.terms.length + 1, start_date: "", end_date: "" }] }));
  const removeTerm = (i) => setForm((f) => ({ ...f, terms: f.terms.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { year: Number(form.year), status: form.status, terms: form.terms };
      if (editing) await api.put(`/academic-years/${editing.id}`, payload);
      else await api.post("/academic-years", payload);
      toast.success("Salvo"); setOpen(false); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (years === null) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-700" />;
  return (
    <div>
      <div className="mb-4 flex justify-end"><Button onClick={openNew} disabled={disabled} data-testid="year-add-button" className="bg-blue-700 hover:bg-blue-800"><Plus className="mr-1.5 h-4 w-4" /> Novo Ano Letivo</Button></div>
      {years.length === 0 ? <EmptyState icon={CalendarDays} title="Nenhum ano letivo" /> : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {years.map((y) => (
            <Card key={y.id} data-testid="year-card" className="card-hover border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <span className="font-display text-3xl font-black text-slate-900">{y.year}</span>
                <StatusBadge status={y.status} />
              </div>
              <div className="mt-3 space-y-1.5">
                {(y.terms || []).map((t, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-xs">
                    <span className="font-semibold text-slate-700">{t.name}</span>
                    <span className="text-slate-400">{t.start_date || "—"} a {t.end_date || "—"}</span>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => openEdit(y)} className="mt-4 w-full"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Editar</Button>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Editar Ano Letivo" : "Novo Ano Letivo"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ano</Label><Input type="number" data-testid="year-input" value={form.year} onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))} /></div>
            </div>
            <div className="flex items-center justify-between"><Label>Períodos (Trimestres)</Label><Button size="sm" variant="ghost" onClick={addTerm}><Plus className="h-4 w-4" /> Adicionar</Button></div>
            {form.terms.map((t, i) => (
              <div key={i} className="grid grid-cols-12 items-end gap-2 rounded-lg border border-slate-200 p-2">
                <div className="col-span-5"><Label className="text-xs">Nome</Label><Input value={t.name} onChange={(e) => setTerm(i, "name", e.target.value)} /></div>
                <div className="col-span-3"><Label className="text-xs">Início</Label><Input type="date" value={t.start_date || ""} onChange={(e) => setTerm(i, "start_date", e.target.value)} /></div>
                <div className="col-span-3"><Label className="text-xs">Fim</Label><Input type="date" value={t.end_date || ""} onChange={(e) => setTerm(i, "end_date", e.target.value)} /></div>
                <div className="col-span-1"><Button size="icon" variant="ghost" onClick={() => removeTerm(i)} className="text-red-500">×</Button></div>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} disabled={saving} data-testid="year-save-button" className="bg-blue-700 hover:bg-blue-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubjectsTab({ subjects, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", is_core: false, workload_hours: "", area: "" });
  const [saving, setSaving] = useState(false);

  const openNew = () => { setEditing(null); setForm({ name: "", code: "", is_core: false, workload_hours: "", area: "" }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setOpen(true); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/subjects/${editing.id}`, { ...form, area: form.area || null });
      else await api.post("/subjects", { ...form, area: form.area || null });
      toast.success("Salvo"); setOpen(false); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (subjects === null) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-700" />;
  return (
    <div>
      <div className="mb-4 flex justify-end"><Button onClick={openNew} disabled={disabled} data-testid="subject-add-button" className="bg-blue-700 hover:bg-blue-800"><Plus className="mr-1.5 h-4 w-4" /> Nova Disciplina</Button></div>
      {subjects.length === 0 ? <EmptyState icon={BookOpen} title="Nenhuma disciplina" /> : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {subjects.map((s) => (
            <Card key={s.id} data-testid="subject-card" className="card-hover flex items-center justify-between border-slate-200 p-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-slate-800">{s.name}</span>
                  {s.is_core && <Star className="h-4 w-4 fill-orange-400 text-orange-400" />}
                </div>
                <div className="text-xs text-slate-400">{s.code || "—"} {s.is_core && "· Principal"}{s.workload_hours ? ` · ${s.workload_hours} h/ano` : ""}{s.area ? ` · ${AREA_LABELS[s.area]}` : ""}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`subject-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Editar Disciplina" : "Nova Disciplina"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Nome *</Label><Input data-testid="subject-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div><Label>Código</Label><Input value={form.code || ""} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} /></div>
            <div><Label>Carga horária anual (horas)</Label><Input type="number" min={0} data-testid="subject-workload-input" value={form.workload_hours ?? ""} onChange={(e) => setForm((f) => ({ ...f, workload_hours: e.target.value === "" ? null : Number(e.target.value) }))} /></div>
            <div><Label>Área de conhecimento</Label>
              <Select value={form.area || "AUTO"} onValueChange={(v) => setForm((f) => ({ ...f, area: v === "AUTO" ? "" : v }))}>
                <SelectTrigger data-testid="subject-area-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AUTO">Automática (pelo nome)</SelectItem>
                  {Object.entries(AREA_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
              <div><Label>Disciplina principal</Label><p className="text-xs text-slate-400">Considerada na Excelência Acadêmica</p></div>
              <Switch checked={form.is_core} onCheckedChange={(v) => setForm((f) => ({ ...f, is_core: v }))} data-testid="subject-core-switch" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} disabled={saving || !form.name} data-testid="subject-save-button" className="bg-blue-700 hover:bg-blue-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
